import logo from "assets/images/paps-loading-icon.png";
import vfs from "assets/vfs";
import { PlaceType } from "components/GeoAutoCompletion";
import { STORAGE, TOKEN_KEY } from "config/auth";
import { PAPS_AIRBILL_INFO, QueryCategoriesApi, SN_BIGIN_PHONE_NUMBER_REG } from "config/constants";
import PARCEL_TEMPLATE_HEADER, { PRODUCT_TEMPLATE_HEADER } from "config/parcel-template";
import { format } from "date-fns";
import { TemplateResultType } from "domains/Job/utils";
import FileSaver from "file-saver";
import {
  Address,
  Category,
  Client,
  Job,
  Order,
  Parcel,
  ParcelSize,
  Product,
  Receiver,
  TaskVehicleType,
  Vehicle_Type,
  Weight,
} from "generated/graphql";
import { MotorCycleIcon, TreeCycleIcon, VanIcon, VanXlIcon } from "icons";
import { CountryCode, getCountryCallingCode, isValidNumber } from "libphonenumber-js";
import moment from "moment";
import Papa from "papaparse";
// @ts-ignore
import * as pdfMake from "pdfmake/build/pdfmake";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import * as XLSX from "xlsx";
import * as yup from "yup";

/**
 * Will return a phone number in this format : `+221 77 500 40 30` or `+226 77 40 50 60`
 * Take a look at the function if you want to support another country format.
 * Adapted from https://stackoverflow.com/a/8358141/6640967
 * @param phoneNumberString string
 */
export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
  const matchForSenegal = cleaned.match(/^(221)?(\d{2})(\d{3})(\d{2})(\d{2})$/);
  const matchForBurkina = cleaned.match(/^(226)?(\d{2})(\d{2})(\d{2})(\d{2})$/);
  const matchForIvoryCost = cleaned.match(/^(225)?(\d{2})(\d{2})(\d{2})(\d{4})$/);
  const matchForBenign = cleaned.match(/^(229)?(\d{2})(\d{2})(\d{2})(\d{2})$/);
  const matchForGuinea = cleaned.match(/^(224)?(\d{3})(\d{2})(\d{2})(\d{2})$/);
  const matchForTogo = cleaned.match(/^(228)?(\d{2})(\d{2})(\d{2})(\d{2})$/);
  const matchForMali = cleaned.match(/^(223)?(\d{2})(\d{2})(\d{2})(\d{2})$/);

  //
  if (matchForSenegal) {
    return [
      "+221",
      " ",
      matchForSenegal[2],
      " ",
      matchForSenegal[3],
      " ",
      matchForSenegal[4],
      " ",
      matchForSenegal[5],
    ].join("");
  }

  if (matchForIvoryCost) {
    return [
      "+225",
      " ",
      matchForIvoryCost[2],
      " ",
      matchForIvoryCost[3],
      " ",
      matchForIvoryCost[4],
      " ",
      matchForIvoryCost[5],
    ].join("");
  }
  if (matchForBurkina) {
    //
    return [
      "+226",
      " ",
      matchForBurkina[2],
      " ",
      matchForBurkina[3],
      " ",
      matchForBurkina[4],
      " ",
      matchForBurkina[5],
    ].join("");
  }

  if (matchForBenign) {
    //
    return [
      "+229",
      " ",
      matchForBenign[2],
      " ",
      matchForBenign[3],
      " ",
      matchForBenign[4],
      " ",
      matchForBenign[5],
    ].join("");
  }

  if (matchForGuinea) {
    //
    return [
      "+224",
      " ",
      matchForGuinea[2],
      " ",
      matchForGuinea[3],
      " ",
      matchForGuinea[4],
      " ",
      matchForGuinea[5],
    ].join("");
  }

  if (matchForMali) {
    //
    return ["+223", " ", matchForMali[2], " ", matchForMali[3], " ", matchForMali[4], " ", matchForMali[5]].join("");
  }

  if (matchForTogo) {
    //
    return ["+228", " ", matchForTogo[2], " ", matchForTogo[3], " ", matchForTogo[4], " ", matchForTogo[5]].join("");
  }

  return phoneNumberString;
}

export const parseToInt = (number: number) => parseInt(number?.toString(), 10) || 0;

export function sortByDescendant<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
export function stablilizeTableSorting<T extends any[]>(array: T, cmp: (a: any, b: any) => number) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}
export function getTableSorting(order: "asc" | "desc", orderBy: any): (a: any, b: any) => number {
  return order === "desc" ? (a, b) => sortByDescendant(a, b, orderBy) : (a, b) => -sortByDescendant(a, b, orderBy);
}
export function makeSearch<T extends any[]>(items: T, term: string, columnsToSearchIn?: string[]) {
  const getNeedles = (obj: any): boolean =>
    Object.keys(obj).some((key) => {
      const haystack =
        obj[key] !== null && typeof obj[key] === "object"
          ? // Go full recursive when the key is an object ✨
            getNeedles(obj[key])
          : String(obj[key]).toLowerCase().includes(term.toLocaleLowerCase());

      return haystack;
    });

  return items?.filter((item) => getNeedles(item)) as T;
}

export function itExistOnList(list: Receiver[], receiver: Receiver) {
  return (
    list.findIndex(
      (element) =>
        element.receiver_phone_number === receiver.receiver_phone_number &&
        element.receiver_firstname === receiver.receiver_firstname &&
        element.receiver_lastname === receiver.receiver_lastname
    ) !== -1
  );
}

export const isToday = (date: string) => moment(date).isSame(moment(), "day");

const QRCode = require("qrcode");

export const currencyFormat = (num: number) => {
  const numberToFormat = String(num);
  if (typeof numberToFormat !== "string") {
    return "00 CFA";
  }
  return `${parseInt(numberToFormat, 10)
    .toFixed(0)
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")} CFA`;
};

export function generateAirwayBills(
  data: Parcel[],
  job: Job,
  client: Client,
  countryCode: CountryCode = "SN",
  jobData?: Job
) {
  const textToBase64Barcode = (text: string) => {
    const canvas = document.createElement("canvas");
    QRCode.toCanvas(canvas, text, {
      errorCorrectionLevel: "H",
      type: "image/png",
      quality: 0.3,
      margin: 1,
    });
    return canvas;
  };

  const Day = moment().format("l");
  const Hour = moment().format("LTS");

  const documentDefinition: TDocumentDefinitions = {
    info: {
      title: client?.client_company_name,
      author: client?.client_contact_firstname,
    },
    pageSize: "A5",
    pageMargins: [20, 20, 20, 20],
    content: [
      data.map((item, index) => [
        {
          layout: "noBorders", // optional
          table: {
            widths: [369],
            heights: [20],
            body: [
              [
                [
                  {
                    fontSize: 20,
                    bold: true,
                    text: "Bordereau de livraison",
                    margin: [5, 0, 0, 0],
                  },
                ],
              ],
            ],
          },
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [55, 300],
            heights: [55, 50],
            body: [
              [
                [
                  {
                    image: logo,
                    width: 55,
                    height: 55,
                    margin: [5, 3, 0, 0],
                  },
                ],
                [
                  {
                    width: 300,
                    height: 50,
                    fontSize: 8,
                    margin: [5, 0, 0, 0],
                    text: PAPS_AIRBILL_INFO[countryCode],
                  },
                ],
              ],
            ],
          },
        },
        {
          table: {
            headerRows: 1,
            widths: [369],
            body: [[""], [""]],
          },
          layout: "headerLineOnly",
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [80, 280],
            heights: [100, 100],
            body: [
              [
                [
                  {
                    image: textToBase64Barcode(item.parcel_UID).toDataURL(),
                    width: 80,
                    height: 80,
                    margin: [5, 3, 0, 0],
                  },
                ],
                [
                  {
                    fontSize: 17,
                    bold: true,
                    text: item.parcel_UID,
                    margin: [5, 0, 0, 0],
                  },
                  {
                    layout: "noBorders", // optional
                    table: {
                      widths: [260],
                      heights: [60],
                      body: [
                        [
                          [
                            {
                              bold: false,
                              fontSize: 11,
                              margin: [5, 1, 0, 20],
                              text: [
                                { text: "Taille :", bold: true },
                                `${item?.parcel_size} \n`,
                                { text: "Description :", bold: true, alignment: "justify" },
                                `${item?.parcel_description} \n`,
                                { text: " A Collecter :", bold: true },
                                `${currencyFormat(item?.parcel_amount_to_collect) || "N/A"} \n`,
                                { text: "Info additionnelles :", bold: true },
                                `${item?.parcel_additional_info || "N/A"} \n`,
                              ],
                            },
                          ],
                        ],
                      ],
                    },
                  },
                ],
              ],
            ],
          },
        },
        item?.relay
          ? {
              layout: "noBorders", // optional
              table: {
                widths: [90, 260],
                heights: [11, 9],
                body: [
                  [
                    [
                      {
                        width: 90,
                        height: 11,
                        margin: [0, 0, 0, 0],
                        bold: true,
                        text: "Hub de livraison",
                      },
                    ],
                    [
                      {
                        width: 260,
                        height: 9,
                        margin: [4, 0, 0, 0],
                        canvas: [{ type: "line", x1: 0, y1: 10, x2: 260, y2: 10, lineWidth: 1 }],
                      },
                    ],
                  ],
                ],
              },
            }
          : null,
        item?.relay
          ? {
              layout: "noBorders", // optional
              table: {
                widths: [369],
                heights: [50],
                body: [
                  [
                    [
                      {
                        fontSize: 11,
                        height: 50,
                        margin: [5, 1, 0, 0],
                        text: [
                          { text: "Type :", bold: true },
                          `${item.relay?.types || "N/A"} \n`,
                          { text: "Name :", bold: true },
                          `${item.relay?.name || "N/A"} \n`,
                          { text: "Adresse :", bold: true },
                          `${item.relay?.location?.address || "N/A"} \n`,
                        ],
                      },
                    ],
                  ],
                ],
              },
            }
          : null,
        {
          layout: "noBorders", // optional
          table: {
            widths: [60, 300],
            heights: [11, 9],
            body: [
              [
                [
                  {
                    width: 50,
                    height: 11,
                    margin: [0, 0, 0, 0],
                    bold: true,
                    text: "Expéditeur",
                  },
                ],
                [
                  {
                    width: 300,
                    height: 9,
                    margin: [4, 0, 0, 0],
                    canvas: [{ type: "line", x1: 0, y1: 10, x2: 300, y2: 10, lineWidth: 1 }],
                  },
                ],
              ],
            ],
          },
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [369],
            heights: [50],
            body: [
              [
                [
                  {
                    fontSize: 11,
                    height: 50,
                    margin: [5, 1, 0, 0],
                    text: [
                      { text: "Société :", bold: true },
                      { text: `${client?.client_company_name || "N/A"} \n` },
                      { text: "Prénom et Nom :", bold: true },
                      {
                        text: `${item?.pickupMoreInfo?.firstName || client?.client_contact_firstname} ${
                          item?.pickupMoreInfo?.lastName || client?.client_contact_lastname || "N/A"
                        }\n`,
                      },
                      { text: "Téléphone :", bold: true },
                      `${item?.pickupMoreInfo?.phoneNumber || client?.client_phone_number || "N/A"} \n`,
                      { text: "Adresse :", bold: true },
                      `${item?.pickupAddress?.address || client?.client_address?.address || "N/A"}\n`,
                    ],
                  },
                ],
              ],
            ],
          },
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [60, 300],
            heights: [10, 10],
            body: [
              [
                [
                  {
                    width: 50,
                    height: 11,
                    margin: [0, 0, 0, 0],
                    bold: true,
                    text: "Receveur",
                  },
                ],
                [
                  {
                    width: 300,
                    height: 11,
                    margin: [2, 0, 0, 0],
                    canvas: [{ type: "line", x1: 0, y1: 10, x2: 300, y2: 10, lineWidth: 1 }],
                  },
                ],
              ],
            ],
          },
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [369],
            heights: [70],
            body: [
              [
                [
                  {
                    fontSize: 11,
                    height: 70,
                    margin: [5, 1, 0, 20],
                    text: [
                      { text: "Prénom et Nom :", bold: true },
                      `${item?.parcel_order?.order_receiver?.receiver_firstname} ${item?.parcel_order?.order_receiver?.receiver_lastname} \n`,
                      { text: "Société :", bold: true },
                      `${item.parcel_order?.order_receiver?.receiver_entreprise || "N/A"} \n`,
                      { text: "Adresse :", bold: true },
                      `${item?.parcel_order?.order_receiver?.receiver_address?.address || "N/A"} \n`,
                      { text: "Adresse Complémentaire :", bold: true },
                      `${item?.parcel_order?.order_receiver?.receiver_address?.additional_address  || item?.parcel_order?.order_receiver?.receiver_specification_address || "N/A"} \n`,
                      { text: "Référence Client :", bold: true },
                      `${item?.parcel_client_reference || "N/A"} \n`,
                      { text: "Additional info :", bold: true },
                      `${item.parcel_additional_info || "N/A"}\n`,
                    ],
                  },
                ],
              ],
            ],
          },
        },
        {
          table: {
            headerRows: 1,
            widths: [369],
            body: [[""], [""]],
          },
          layout: "headerLineOnly",
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [369],
            heights: 20,
            body: [
              [
                {
                  fontSize: 11,
                  bold: true,
                  margin: [5, 0, 0, 0],
                  text: `
                Adresse mise à jour :
                Date de livraison mise à jour :`,
                },
              ],
            ],
          },
        },
        {
          layout: "noBorders", // optional
          table: {
            widths: [369],
            heights: 10,
            body: [
              [
                {
                  fontSize: 10,
                  margin: [5, 0, 0, 0],
                  text: `Émis le ${Day} à ${Hour}`,
                },
              ],
            ],
          },
          pageBreak: index + 1 !== data?.length ? "after" : null,
        },
      ]),
    ],
  };

  return pdfMake.createPdf(documentDefinition, null, null, vfs);
}

export const fetchGooglePlace = (request: { input: string }): Promise<PlaceType[] | Error> =>
  new Promise((resolve, reject) => {
    try {
      return new (window as any).google.maps.places.AutocompleteService().getPlacePredictions(
        { ...request, componentRestrictions: { country: ["sn", "ci", "bj", "gn", "ml", "tg"] } },
        (results?: PlaceType[]) => resolve(results)
      );
    } catch (error) {
      reject(new Error(error as string));
    }
  });

export const uuid = () => Math.random().toString(36).substr(2, 9);

const getParcel = (row: any): Parcel => ({
  parcel_size: row["SIZE(S-M-L-XL-D)"] || null,
  parcel_description: row.DESCRIPTION || "",
  parcel_additional_info: row.ADDITIONALINFOS || "",
  parcel_value: row.VALUE || 0,
  parcel_amount_to_collect: row.AMOUNTOCOLLECT || 0,
  parcel_client_reference: row.CLIENTREFERENCE || "",
  _id: uuid(),
});

export const PAPS_COUNTRIES: CountryCode[] = ["SN", "CI", "BJ", "GN", "ML", "TG"];

const getPhoneNumber = (phoneNumber: string, countryCallingCode: string): string => {
  let phone: string = phoneNumber;

  if (phone?.length === 9) {
    if (countryCallingCode === "225") {
      phone = `0${phone}`;
    }

    phone = `${countryCallingCode}${phone}`;
  }

  phone = phone.replace(/^(00)/g, "");

  return formatPhoneNumber(phone)?.replace(/(?!\+)\D/g, "");
};

const getReceiverAddress = (res: PlaceType[]): Address => ({
  address: res[0].description,
  place_id: res[0].place_id,
});

const getReceiver = (row: any, countryCallingCode: string): Receiver => ({
  receiver_firstname: `${row.FIRSTNAME ?? ""}`,
  receiver_lastname: `${row.LASTNAME ?? ""}`,
  receiver_entreprise: `${row.ENTERPRISE ?? "N/A"}`,
  receiver_phone_number: row.PHONENUMBER ? getPhoneNumber(`${row.PHONENUMBER}`, countryCallingCode) : null,
  receiver_address: {
    address: `${row.ADDRESS}`,
    city: `${row.CITY}`,
  },
  receiver_specification_address: `${row.ADDRESS}, ${row.CITY}`,
});

const receiverWithAddress = async (receiver: Receiver) => {
  const newReceiver: Receiver = { ...receiver, receiver_address: null };
  if (receiver.receiver_address.city && receiver.receiver_address.address) {
    const res: any = await fetchGooglePlace({ input: receiver.receiver_specification_address });
    if (res?.length) {
      newReceiver.receiver_address = getReceiverAddress(res);
    }
  }
  return newReceiver;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const reducerProcessRow = async (data: any, groupedRow: any) => {
  data = await data;
  const parcels: any = [];
  const receiver = await getReceiver(groupedRow[0], data.countryCallingCode);

  groupedRow.forEach(async (row: any) => {
    await parcels.push(getParcel(row));
  });
  await sleep(100);
  const resultAddress = await receiverWithAddress(receiver);
  await data.orders.push({
    _id: uuid(),
    order_receiver: resultAddress,
    order_parcels: parcels,
  });

  data.newCount += 1;

  return Promise.resolve(data);
};

const groupBy = (arr: any, prop1: string, prop2: string, prop3: string) => {
  const map = new Map(Array.from(arr, (obj) => [`${obj[prop1]}${obj[prop2]}${obj[prop3]}`, []]));
  arr.forEach((obj: any) => map.get(`${obj[prop1]}${obj[prop2]}${obj[prop3]}`).push(obj));
  return Array.from(map.values());
};
export const isValidPhoneNumber = (phone: string): boolean => phone && isValidNumber(phone) && phone?.length > 5;

export const ValidReceiver = yup.object({
  receiver_firstname: yup.string().required(),
  receiver_lastname: yup.string().required(),
  receiver_phone_number: yup
    .string()
    .required()
    .test("is-valid-phone", "", (value: any) => isValidPhoneNumber(value)),
  receiver_entreprise: yup.string().nullable(),
  receiver_address: yup.object({
    place_id: yup.string().required(),
    address: yup.string().required(),
  }),
  receiver_specification_address: yup.string().nullable(),
});

export const ValidParcel = yup.object({
  _id: yup.string(),
  parcel_description: yup.string().required(),
  parcel_size: yup.mixed().oneOf(Object.values(ParcelSize)),
  parcel_value: yup.number().required(),
  parcel_amount_to_collect: yup.number().required(),
  parcel_client_reference: yup.string().nullable(),
});

export const ValidOrder = yup.object({
  order_receiver: ValidReceiver,
  order_parcels: yup.array().of(ValidParcel).min(1),
});
const groupOrdersByPhoneNumber = (orders: Order[]) => {
  const map: Map<string, Order[]> = new Map(
    Array.from(orders, (order) => [
      `${order.order_receiver.receiver_phone_number}${order.order_receiver.receiver_firstname}${order.order_receiver.receiver_lastname}`,
      [],
    ])
  );
  orders.forEach((order: any) =>
    map
      .get(
        `${order.order_receiver.receiver_phone_number}${order.order_receiver.receiver_firstname}${order.order_receiver.receiver_lastname}`
      )
      .push(order)
  );
  const groupedOrders = Array.from(map.values());
  return groupedOrders.map((group) => {
    const parcels: Parcel[] = [];
    group.forEach((order) => {
      parcels.push(...order.order_parcels);
    });
    const finalOrder = group[0];
    finalOrder.order_parcels = parcels;
    return finalOrder;
  });
};

export const parseTemplate = async (
  rows: any[],
  entityList?: Order[],
  countryCallingCode?: string
): Promise<TemplateResultType> => {
  const groupedRowsByPhoneNumber = groupBy(rows, "PHONENUMBER", "FIRSTNAME", "LASTNAME");
  const orders = await groupedRowsByPhoneNumber.reduce(
    reducerProcessRow,
    Promise.resolve({ orders: entityList || [], newCount: 0 })
  );
  const sortedOrders = orders.orders
    .map((order: any) => {
      return { ...order, isValid: ValidOrder.isValidSync(order) };
    })
    .sort((a: any) => Number(a.isValid) - 1);

  const validOrdersCount = sortedOrders.filter((order: any) => order.isValid).length;
  return {
    data: sortedOrders,
    newCount: orders.newCount,
    count: orders.orders.length,
    valid: validOrdersCount,
    invalid: orders.orders.length - validOrdersCount,
  };
};

const fetchGraphqlApi = (query: string) =>
  new Promise<any>((resolve) => {
    fetch(process.env.REACT_APP_GRAPHQL_HTTP_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      },
      body: JSON.stringify({ query }),
    })
      .then((r) => r.json())
      .then((data) => resolve(data))
      .catch(() => resolve(null));
  });

const findCategory = (categoryName: string, list: Category[]): Category => {
  if (!Array.isArray(list) || list.length === 0 || categoryName === null || categoryName.length === 0) {
    return null;
  }
  return list.find((cat) =>
    cat.translations.some((translate) => translate.text.toLocaleLowerCase() === categoryName.toLocaleLowerCase())
  );
};

export const isGreatterThan = (value: any, test = 0) => {
  const num = Number(value);
  return typeof num === "number" && num > test;
};

export const extractString = (value: any) =>
  typeof value === "string" && value != null && value.length ? value : null;

const setProduct = (data: any, list: Category[]): Product => ({
  name: extractString(data[0]),
  reference: data[1]?.length ? data[1] : undefined,
  description: extractString(data[2]),
  category: findCategory(data[3], list),
  volume: isGreatterThan(data[4], 1) ? Number(data[4]) : 1,
  value: isGreatterThan(data[5], 25) ? Number(data[5]) : 25,
  weight: Array.from(Object.values(Weight)).some((e) => e === data[6]?.toUpperCase())
    ? data[6]?.toUpperCase()
    : Weight.S,
});
export async function parseTemplateProduct(
  data: any,
  originProducts: Product[],
  callback?: (products: any, success: boolean) => void
) {
  let products = [...originProducts];
  if (!Array.isArray(data) || data[0].length === 0 || data[0].length !== PRODUCT_TEMPLATE_HEADER.length) {
    callback(products, false);
  }
  const response = await fetchGraphqlApi(QueryCategoriesApi);
  const { categories } = response.data.allCategories.data;
  const newProducts = data.map((e: any) => setProduct(e, categories));
  newProducts.shift();
  products = [...products, ...newProducts];
  const filteredProducts = products.filter((p) => p.name != null && p.description != null && p.category != null);
  callback(filteredProducts, Boolean(filteredProducts.length));
}

function isValidExcelTemplateHeader(original: string, incoming: string): boolean {
  return original.toLowerCase().trim() === incoming.toLowerCase().trim();
}

function extractHeader(ws: XLSX.WorkSheet) {
  const header = [];
  const columnCount = XLSX.utils.decode_range(ws["!ref"]).e.c + 1;
  for (let i = 0; i < columnCount; ++i) {
    header[i] = ws[`${XLSX.utils.encode_col(i)}1`]?.v;
  }
  return header;
}

export const uploadTemplate = (
  file: File,
  entityList: any[],
  countryCode: CountryCode,
  cb: (data: TemplateResultType, error?: Error) => void
) => {
  const reader = new FileReader();
  const rABS = !!reader.readAsArrayBuffer;
  reader.onload = async (e) => {
    const bstr: string | ArrayBuffer = e.target.result;
    const workbook = XLSX.read(bstr, {
      type: rABS ? "binary" : "array",
      raw: true,
    });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const data: any[] = XLSX.utils.sheet_to_json(worksheet);
    const header = extractHeader(worksheet);
    const isHeaderTemplateCorrect = isValidExcelTemplateHeader(PARCEL_TEMPLATE_HEADER.join(""), header.join(""));
    if (data.length) {
      if (isHeaderTemplateCorrect) {
        try {
          const countryCallingCode = getCountryCallingCode(countryCode);
          const parsedData = await parseTemplate(data, entityList);
          cb(parsedData);
        } catch {
          cb(null, new Error("unkown"));
        }
      } else {
        cb(null, new Error("header"));
      }
    } else {
      cb(null, new Error("empty"));
    }
  };
  if (rABS) reader.readAsBinaryString(file);
  else reader.readAsArrayBuffer(file);
};

export function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

export const isWeekEnd = (day: string) => {
  const date = moment(day);
  if (date.isValid()) {
    return date.isoWeekday() === 7;
  }
  return false;
};

export const isMorning = (date = new Date()) => date.getHours() < 11;

export const isAfternoom = (date = new Date()) => date.getHours() >= 15;

export const getHours = (time: string) => Number(time.split(":")[0]);

export const isCurrentDay = (day: string) => {
  const date = moment(day);
  if (date.isValid()) {
    return date.date() === moment().date();
  }
  return false;
};

export function ValidateEmail(email: string) {
  if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email)) {
    return true;
  }
  return false;
}

export const reformatPhoneSn = (phone: string) => {
  if (phone && phone.length !== 0) {
    let value = phone.replace(/\D/g, "");
    value = value.replace(SN_BIGIN_PHONE_NUMBER_REG, "");
    return value;
  }
  return phone;
};

export const truncateMessage = (message: string, truncate = 20) =>
  message?.length > truncate ? `${message.substr(0, truncate)}...` : message;

export const getUrlUpload = (): string => process.env.REACT_APP_FILE_URL;

export const uploadFile = (image: any) =>
  new Promise<string>((resolve) => {
    if (image === null) return resolve(null);
    const formData = new FormData();
    formData.append("file", image, image.name);
    fetch(getUrlUpload(), { method: "POST", body: formData })
      .then((response) => {
        if (response.ok) {
          response.text().then((textResponse) => {
            const url = JSON.parse(textResponse);
            resolve(url.url);
          });
        } else {
          resolve(null);
        }
      })
      .catch(() => resolve(null));
  });

export const uploadFileFunction = (file: File, template: string[]) =>
  new Promise<{ data: any; success: boolean }>((resolve, reject) => {
    if (file != null) {
      Papa.parse(file, {
        complete: (results) => {
          if (results.data.length > 0) {
            const data = results.data as any;
            if (data.length === 1) {
              reject(Error("Error to parse file"));
            } else if (template.join(",").toLowerCase() === data[0].join(",").toLowerCase()) {
              parseTemplateProduct(data, [], (entities, success) => {
                resolve({ data: entities, success });
              });
            } else {
              reject(Error("Error to parse file"));
            }
          }
        },
        error: (error) => reject(error),
      });
    }
  });

const downloadBlob = (url: any, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, 150);
  };
  a.addEventListener("click", clickHandler, false);
  a.click();

  return a;
};

export const extractDataToExcel = (fileName: string, csvData: any[]) => {
  const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const ws = XLSX.utils.json_to_sheet(csvData);
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

export const downloadTemplate = (url: string, name: string) => {
  const downloadLink = downloadBlob(url, name);
  document.body.appendChild(downloadLink);
};

export const renderImageArticle = (image: any) => (typeof image === "string" ? image : URL.createObjectURL(image));

export const saveGiftToLocal = (gift: any) => {
  STORAGE.setItem("gift", JSON.stringify(gift));
};

export const getLocalGift = () => {
  const gift = JSON.parse(STORAGE.getItem("gift"));
  return gift;
};

export function a11yProps(index: any) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

export const formatAmount = (value: any) => {
  return value ? new Intl.NumberFormat("fr-FR", { currency: "XOF", style: "currency" }).format(value) : "0 CFA";
};

/**
 *
 * @param {T} date
 * @param {string} formatStr
 * @returns {date}
 */
export function formatDate<T extends string | number | Date>(date: T, formatStr: string) {
  try {
    return format(new Date(date), formatStr, {
      useAdditionalWeekYearTokens: true,
      useAdditionalDayOfYearTokens: true,
    });
  } catch (error) {
    return null;
  }
}

export const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(String(email).toLowerCase());

export const truncateString = (str: string, limit: number = 10) => {
  if (typeof str === "string") {
    if (str.length < limit) return str;
    return str ? `${str.slice(0, limit)}... ` : str;
  }
};

export const displayFullName = (firstname: string, lastname: string) => {
  return !firstname && !lastname ? "N/A" : `${firstname} ${lastname}`;
};

export const VehiculesTypeIcon = (vehicleType: Vehicle_Type | TaskVehicleType | any, size: number) => {
  switch (vehicleType) {
    case TaskVehicleType.Scooter:
      return <MotorCycleIcon width={size} color="#399ebf" />;
    case TaskVehicleType.Van:
      return <VanXlIcon width={size} color="#399ebf" />;
    case TaskVehicleType.MiniVan:
      return <VanIcon width={size} color="#399ebf" />;
    case TaskVehicleType.Tricycle:
      return <TreeCycleIcon width={size} color="#399ebf" />;
    default:
      break;
  }
};
